import { useEffect, useState } from "react";
import { Nav, Navbar, NavDropdown } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { useMemo } from "react";

import useNavbarFixedPadding from "../hooks/userNavbarFixedPadding";
import LocalStorage from "../local-storage";
import { clearCategories, clearSeniorHomes, logoutUser, ResetBlog, ResetCoupons, ResetOrders, ResetShoppingcart, ResetUsers } from "../redux/actions";
import { AppState } from "../redux/types";
import { getLocaleFromPath, getSlug, Locale, localePath } from "../i18n/config";
import { ROLE } from "../utils/constants";
import Logo from "./Logo";
import LocaleSelector from "./localeSelector/LocaleSelector";

const navLabels: Record<string, Record<Locale, string>> = {
    osta:           { fi: "Osta", en: "Buy", sv: "Köp" },
    tablet:         { fi: "Senioritabletti", en: "Senior Tablet", sv: "Seniorplatta" },
    phone:          { fi: "Senioripuhelin", en: "Senior Phone", sv: "Seniortelefon" },
    alarm:          { fi: "Turvaranneke", en: "Safety Bracelet", sv: "Säkerhetsarmband" },
    app:            { fi: "Videopuhelu Appi", en: "Video Call App", sv: "Videosamtalsapp" },
    otherServices:  { fi: "Muut palvelut", en: "Other Services", sv: "Övriga tjänster" },
    memoryGame:     { fi: "Muistipeli", en: "Memory Game", sv: "Minnesspel" },
    accessories:    { fi: "Lisätarvikkeet", en: "Accessories", sv: "Tillbehör" },
    forCompanies:   { fi: "Yrityksille", en: "For Businesses", sv: "För företag" },
    aboutUs:        { fi: "Meistä", en: "About Us", sv: "Om oss" },
    blog:           { fi: "Blogi", en: "Blog", sv: "Blogg" },
    contact:        { fi: "Yhteydenotto", en: "Contact", sv: "Kontakt" },
    faq:            { fi: "Usein kysyttyä", en: "FAQ", sv: "Vanliga frågor" },
    loggedIn:       { fi: "Kirjautunut", en: "Logged In", sv: "Inloggad" },
    account:        { fi: "Tili", en: "Account", sv: "Konto" },
    logout:         { fi: "Kirjaudu ulos", en: "Log Out", sv: "Logga ut" },
    login:          { fi: "Kirjaudu", en: "Log In", sv: "Logga in" },
};

const NavBar = () => {
    const user = useSelector((state: AppState) => state.user);
    const history = useHistory();
    const { pathname } = useLocation();
    const locale = getLocaleFromPath(pathname);

    const t = (key: string) => navLabels[key]?.[locale] ?? navLabels[key]?.fi ?? key;

    const paths = useMemo(() => ({
        osta:            localePath("osta"),
        senioritabletti: localePath("senioritabletti"),
        senioripuhelin:  localePath("senioripuhelin"),
        turvaranneke:    localePath("turvaranneke"),
        videopuhelu_appi: localePath("videopuhelu_appi"),
        muistipeli:      localePath("muistipeli"),
        osta_lisatarvikkeet: localePath("osta") + "/" + getSlug("lisatarvikkeet", locale),
        yrityksille:     localePath("yrityksille"),
        aboutus:         localePath("aboutus"),
        happysenior_blogi: localePath("happysenior_blogi"),
        yhteydenotto:    localePath("yhteydenotto"),
        usein_kysyttya:  localePath("usein_kysyttya"),
        tili_tilaus:     localePath("tili", "tilaus"),
        kirjaudu:        localePath("kirjaudu"),
        home:            localePath("home"),
    }), [pathname, locale]); // eslint-disable-line react-hooks/exhaustive-deps
    const dispatch = useDispatch();
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        setExpanded(false);
    }, [pathname]);


    const handleLogout = () => {
        LocalStorage.removeToken();
        localStorage.removeItem("initState");
        setExpanded(false);
        dispatch(logoutUser());
        dispatch(ResetShoppingcart());
        dispatch(ResetUsers());
        dispatch(ResetBlog());
        dispatch(ResetCoupons());
        dispatch(ResetOrders());
        dispatch(clearSeniorHomes());
        dispatch(clearCategories());
        history.push(localePath("home"));
    };

    useNavbarFixedPadding();
    return (
        <Navbar expanded={expanded} onToggle={setExpanded} expand="xl" bg="light" variant="light" className="custom-nav">
            <Navbar.Brand as={NavLink} to={paths.home}>
                <Logo />
            </Navbar.Brand>

            <div className="d-xl-none d-flex align-items-center ml-auto">
                <LocaleSelector compact onSelect={() => setExpanded(false)} />
                <Navbar.Toggle aria-controls="responsive-navbar-nav" className="hamburger ml-2" />
            </div>
            <Navbar.Collapse id="responsive-navbar-nav">
                <Nav className="mr-auto">
                    <Nav.Link as={NavLink} to={paths.osta}>
                        {t("osta")}
                    </Nav.Link>
                    <Nav.Link as={NavLink} to={paths.senioritabletti}>
                        {t("tablet")}
                    </Nav.Link>
                    <Nav.Link as={NavLink} to={paths.senioripuhelin}>
                        {t("phone")}
                    </Nav.Link>
                    <Nav.Link as={NavLink} to={paths.turvaranneke}>
                        {t("alarm")}
                    </Nav.Link>
                    <Nav.Link as={NavLink} to={paths.videopuhelu_appi} onClick={() => setExpanded(false)}>
                        {t("app")}
                    </Nav.Link>

                    <NavDropdown title={t("otherServices")} id="muut-palvelut">
                        <NavDropdown.Item as={NavLink} to={paths.muistipeli} onClick={() => setExpanded(false)}>
                            {t("memoryGame")}
                        </NavDropdown.Item>
                        <NavDropdown.Item as={NavLink} to={paths.osta_lisatarvikkeet} onClick={() => setExpanded(false)}>
                            {t("accessories")}
                        </NavDropdown.Item>
                    </NavDropdown>

                    <Nav.Link as={NavLink} to={paths.yrityksille} onClick={() => setExpanded(false)}>
                        {t("forCompanies")}
                    </Nav.Link>

                    <NavDropdown title={t("aboutUs")} id="meistä">
                        <NavDropdown.Item as={NavLink} to={paths.aboutus} onClick={() => setExpanded(false)}>
                            {t("aboutUs")}
                        </NavDropdown.Item>
                        <NavDropdown.Item as={NavLink} to={paths.happysenior_blogi} onClick={() => setExpanded(false)}>
                            {t("blog")}
                        </NavDropdown.Item>
                        <NavDropdown.Item as={NavLink} to={paths.yhteydenotto} onClick={() => setExpanded(false)}>
                            {t("contact")}
                        </NavDropdown.Item>
                    </NavDropdown>

                    <Nav.Link as={NavLink} to={paths.usein_kysyttya} onClick={() => setExpanded(false)}>
                        {t("faq")}
                    </Nav.Link>

                    {user.isLoggedIn ? (
                        <NavDropdown title={t("loggedIn")} id="user-firstname">
                            <NavDropdown.Item as={NavLink} to={paths.tili_tilaus}>
                                {t("account")}
                            </NavDropdown.Item>
                            <NavDropdown.Item onClick={handleLogout}>{t("logout")}</NavDropdown.Item>
                        </NavDropdown>
                    ) : (
                        <Nav.Link as={NavLink} to={paths.kirjaudu}>
                            {t("login")}
                        </Nav.Link>
                    )}
                    {user && (user.isAdmin || user.role === "it_support") && (
                        <NavDropdown title="Admin" id="adminmenu">
                            <NavDropdown.Item as={NavLink} to="/admin/users" onClick={() => setExpanded(false)}>
                                Asiakkaat
                            </NavDropdown.Item>
                            <NavDropdown.Item as={NavLink} to="/admin/services" onClick={() => setExpanded(false)}>
                                Palvelut
                            </NavDropdown.Item>
                            <NavDropdown.Item as={NavLink} to="/admin/orders" onClick={() => setExpanded(false)}>
                                Tilaukset
                            </NavDropdown.Item>
                            <NavDropdown.Item as={NavLink} to="/admin/coupons" onClick={() => setExpanded(false)}>
                                Coupons
                            </NavDropdown.Item>
                            <NavDropdown.Item as={NavLink} to="/admin/senior_homes" onClick={() => setExpanded(false)}>
                                Senior homes
                            </NavDropdown.Item>
                            <NavDropdown.Item as={NavLink} to="/admin/nurse" onClick={() => setExpanded(false)}>
                                Hoitaja
                            </NavDropdown.Item>
                            {user && user.isAdmin && (
                                <>
                                    <NavDropdown.Item as={NavLink} to="/admin/alerts" onClick={() => setExpanded(false)}>
                                        Alerts
                                    </NavDropdown.Item>
                                    {/* <NavDropdown.Item as={NavLink} to="/admin/blogs">
                    Blog
                  </NavDropdown.Item> */}
                                    <NavDropdown.Item as={NavLink} to="/admin/it_support" onClick={() => setExpanded(false)}>
                                        It-tuki
                                    </NavDropdown.Item>
                                </>
                            )}
                        </NavDropdown>
                    )}

                    {user && user.role === ROLE.HEAD_NURSE && (
                        <NavDropdown title="Admin" id="adminmenu">
                            <NavDropdown.Item as={NavLink} to="/admin/users" onClick={() => setExpanded(false)}>
                                Asiakkaat
                            </NavDropdown.Item>
                            <NavDropdown.Item as={NavLink} to="/admin/nurse" onClick={() => setExpanded(false)}>
                                Hoitaja
                            </NavDropdown.Item>
                        </NavDropdown>
                    )}
                    <div className="d-none d-xl-flex">
                        <LocaleSelector onSelect={() => setExpanded(false)} />
                    </div>
                </Nav>
            </Navbar.Collapse>
        </Navbar>
    );
};

export default NavBar;
